import React, { useState, useMemo } from 'react';
import { Building2, X, AlertTriangle, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

// Rótulos canônicos das categorias de "pulado" (SPEC §0 — taxonomia inegociável).
const CATEGORIA_LABEL = {
  ja_vinculado: 'já vinculado',
  sem_empresa: 'domínio sem empresa',
  ambiguo: 'ambíguo',
};

/**
 * Gate de confirmação (D-11) da ação de lote "Vincular empresa" do Inbox.
 *
 * Renderiza o PREVIEW já calculado pelo pai (grupos a vincular + pulados com
 * motivo) e só dispara o apply quando o humano confirma. Este componente NÃO
 * grava nada por si — chama `onConfirm()` do pai, que faz o DML chunked.
 * SPEC §4.3.
 *
 * @param {object} preview — { grupos:[{empresa_id, empresa_nome, count, emailIds, amostras}],
 *                             pulados:[{emailId, categoria, motivo, subject}],
 *                             resumo:{vincular_total, grupos_count, pulados_total} }
 * @param {function} onConfirm — async; pai grava e fecha o modal em sucesso.
 * @param {function} onClose — fecha sem gravar (Cancelar / X).
 */
const BulkVincularEmpresaModal = ({ preview, onConfirm, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [puladosAbertos, setPuladosAbertos] = useState(false);

  const grupos = preview?.grupos || [];
  const pulados = useMemo(() => preview?.pulados || [], [preview]);
  const resumo = preview?.resumo || { vincular_total: 0, grupos_count: 0, pulados_total: 0 };

  // Conta pulados por categoria canônica para o cabeçalho da seção colapsável.
  const puladosPorCategoria = useMemo(() => {
    const m = new Map();
    for (const p of pulados) {
      if (!m.has(p.categoria)) m.set(p.categoria, []);
      m.get(p.categoria).push(p);
    }
    return m;
  }, [pulados]);

  const podeConfirmar = resumo.vincular_total > 0 && !busy;

  const handleConfirm = async () => {
    if (resumo.vincular_total === 0) return;
    setBusy(true);
    try {
      await onConfirm(); // pai grava + fecha o modal em sucesso
    } catch (_e) {
      setBusy(false); // erro: pai mantém o modal aberto; reabilita o botão
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10010] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="p-5 border-b border-slate-200 flex items-start justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Building2 className="text-blue-600" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">
                Vincular {resumo.vincular_total} email{resumo.vincular_total === 1 ? '' : 's'} a{' '}
                {resumo.grupos_count} empresa{resumo.grupos_count === 1 ? '' : 's'}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Revise antes de gravar. Nada é alterado até você confirmar.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Aviso fixo — reforço P25 (vincula EMPRESA, nunca CONTATO) */}
        <div className="mx-5 mt-4 bg-amber-50 border border-amber-200 rounded-md p-2.5 text-xs text-amber-900 flex items-start gap-1.5 flex-shrink-0">
          <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
          <span>
            Vincula apenas a <strong>EMPRESA</strong> do email. Não altera contatos, ofertas
            nem o status de triagem — os emails seguem pendentes.
          </span>
        </div>

        {/* Body */}
        <div className="flex-grow overflow-y-auto p-5 space-y-4">
          {grupos.length > 0 ? (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">Empresa</th>
                    <th className="text-right font-medium px-3 py-2 w-20">Emails</th>
                    <th className="text-left font-medium px-3 py-2">Amostra de assuntos</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {grupos.map((g) => (
                    <tr key={g.empresa_id}>
                      <td className="px-3 py-2 font-semibold text-slate-800 align-top">{g.empresa_nome}</td>
                      <td className="px-3 py-2 text-right align-top">
                        <span className="inline-block bg-blue-100 text-blue-800 rounded-full px-2 py-0.5 text-xs font-semibold">
                          {g.count}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[11px] text-slate-500 align-top">
                        {g.amostras && g.amostras.length > 0 ? (
                          <ul className="space-y-0.5">
                            {g.amostras.map((a, i) => (
                              <li key={i} className="truncate max-w-md">• {a}</li>
                            ))}
                          </ul>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-6 text-sm text-slate-500 border border-dashed border-slate-200 rounded-md">
              Nenhum email da seleção pôde ser resolvido automaticamente.
              {pulados.length > 0 && ' Veja os pulados abaixo.'}
            </div>
          )}

          {/* Pulados — colapsável, com motivo por email (SPEC §4.3) */}
          {pulados.length > 0 && (
            <div className="border border-slate-200 rounded-md">
              <button
                onClick={() => setPuladosAbertos((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2 text-left text-xs font-medium text-slate-600 hover:bg-slate-50"
              >
                <span className="flex items-center gap-1.5">
                  {puladosAbertos ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  Pulados ({pulados.length})
                </span>
                <span className="flex flex-wrap items-center justify-end gap-x-2 text-[10px] text-slate-400">
                  {Array.from(puladosPorCategoria.entries()).map(([cat, arr]) => (
                    <span key={cat}>
                      {CATEGORIA_LABEL[cat] || cat}: {arr.length}
                    </span>
                  ))}
                </span>
              </button>
              {puladosAbertos && (
                <div className="border-t border-slate-100 max-h-52 overflow-y-auto divide-y divide-slate-50">
                  {pulados.map((p, i) => (
                    <div key={p.emailId || i} className="px-3 py-1.5 text-[11px] flex items-start gap-2">
                      <span className="text-slate-700 truncate flex-grow">{p.subject}</span>
                      <span className="text-slate-400 flex-shrink-0">{p.motivo}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer — gate: Cancelar | Confirmar */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={!podeConfirmar}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Building2 size={14} />}
            Confirmar vínculo ({resumo.vincular_total})
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkVincularEmpresaModal;
